import axios from 'axios';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import {Box} from '@mui/material';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useProcessEvent from '@contentlab/hooks/analytics/use-process-event';
import {CONTENT_STUDIO_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import downloadAssets from '@/utils/content-studio/downloadAssets';
import {formattedUnixTimestamp} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {
  DURATION_MINUTES_SECONDS_FORMAT,
  SHORT_MONTH_SHORT_DAY_COMMA_YEAR_DAY_HOUR_MINUTE_FORMAT,
} from '@/config/timeFormats';
import {PreviewAsset} from '@/views/content-studio/components/asset-preview-modal/types';
import styles from './AssetPreviewPanel.module.scss';

dayjs.extend(duration);
dayjs.extend(advancedFormat);

function formatFileSize(bytes?: number): string {
  if (!bytes) return '-';

  const megabytes = bytes / (1024 * 1024);
  return `${megabytes.toFixed(2)}MB`;
}

function formatDuration(milliseconds?: number): string {
  if (!milliseconds) return '-';

  const formattedDuration = dayjs.duration(milliseconds);
  return formattedDuration.format(DURATION_MINUTES_SECONDS_FORMAT);
}

export interface AssetPreviewPanelProps {
  testID?: string;
  asset: PreviewAsset;
  presentationId: string;
}

function AssetPreviewPanel(props: AssetPreviewPanelProps) {
  const {testID = 'asset-preview-panel', asset, presentationId} = props;
  const [downloadInProgress, setDownloadInProgress] = useState<boolean>(false);
  const {uiInteraction} = useUiInteractionEvent();
  const {started, ended} = useProcessEvent();
  const tableData = [
    [translate('Aspect ratio'), asset.metadata?.aspectRatio],
    [translate('Language'), asset.metadata?.language],
    [translate('Source'), asset.originalAssetName],
    [translate('Duration'), formatDuration(asset.metadata?.durationMillis)],
    [translate('Size'), formatFileSize(asset.metadata?.fileSizeBytes)],
    [translate('Format'), asset.name.split('.').pop()?.toUpperCase()],
    [translate('Resolution'), asset.metadata?.resolution],
  ];

  const downloadAsset = async () => {
    const interactionID = uuid();
    const processID = uuid();
    uiInteraction({
      item_name: 'preview_download',
      interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSET_PREVIEW,
      interaction_id: interactionID,
      presentation_id: presentationId,
      asset_id: asset.id,
    });
    started({
      endpoint: asset.url || '',
      process_id: processID,
      process_name: 'download',
      original_id: asset.originalAssetId,
      interaction_id: interactionID,
      asset_id: asset.id,
      process_details: JSON.stringify({
        duration: asset.metadata.durationMillis,
        size: asset.metadata.fileSizeBytes,
        resolution: asset.metadata.resolution,
        aspectRatio: asset.metadata.aspectRatio,
      }),
      source: CONTENT_STUDIO_VIEW_NAMES.ASSET_PREVIEW,
    });
    try {
      setDownloadInProgress(true);
      const response = await axios.get(asset.url || '', {
        responseType: 'blob',
      });
      await downloadAssets(response, asset.name || '');
      ended({
        endpoint: asset.url || '',
        process_id: processID,
        process_name: 'download',
        result: 'success',
        outcome_id: asset.id,
        original_id: asset.originalAssetId,
      });
    } catch (error) {
      ended({
        endpoint: asset.url || '',
        process_id: processID,
        process_name: 'download',
        result: 'failure',
        error: error instanceof Error ? error.message : String(error),
        outcome_id: asset.id,
        original_id: asset.originalAssetId,
      });
    } finally {
      setDownloadInProgress(false);
    }
  };

  return (
    <Box className={styles.panelContainer} data-testid={testID}>
      <Box className={styles.descriptionContainer}>
        <Box className={styles.headerContainer}>
          <Headline size="sm">{asset.name}</Headline>
          {asset.createdAt && (
            <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
              {formattedUnixTimestamp(
                asset.createdAt,
                SHORT_MONTH_SHORT_DAY_COMMA_YEAR_DAY_HOUR_MINUTE_FORMAT
              )}
            </Body>
          )}
        </Box>
        <table className={styles.table}>
          <tbody className={styles.tbody}>
            {tableData.map(([key, value]) => (
              <tr key={key} className={styles.tableRow}>
                <td className={styles.mainTableCell}>{key}</td>
                <td className={styles.secondaryTableCell}>{value || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Button
        className={styles.downloadButton}
        appearance="neutral"
        mode="filled"
        size="large"
        icon={<Icon size="medium" appearance="white" name="Actions-LooksOff" />}
        iconPosition="end"
        onClick={downloadAsset}
        isLoading={downloadInProgress}
        disabled={!asset.url || downloadInProgress}
      >
        {translate('Download')}
      </Button>
    </Box>
  );
}

export default AssetPreviewPanel;
